/**
 * PatternSelector - Sélecteur de patterns A-H avec copy/paste/clear
 *
 * Permet de:
 * - Changer de pattern (A-H)
 * - Copier le pattern courant
 * - Coller un pattern copié
 * - Effacer le pattern courant
 */

export class PatternSelector {
    constructor(store, onPatternChange = null) {
        this.store = store;
        this.onPatternChangeCallback = onPatternChange;
        this.container = null;
        this.isCollapsed = localStorage.getItem('pattern_collapsed') === 'true';

        // S'abonner aux changements de pattern
        this.unsubscribe = this.store.onPatternChange((patternId) => {
            this.updateDisplay();
            if (this.onPatternChangeCallback) {
                this.onPatternChangeCallback(patternId);
            }
        });
    }

    render(container) {
        if (typeof container === 'string') {
            this.container = document.getElementById(container);
        } else {
            this.container = container;
        }

        if (!this.container) return;

        const currentPattern = this.store.getCurrentPatternId();
        const bankIds = this.store.getPatternBankIds();

        this.container.innerHTML = `
            <div class="machine pattern-machine ${this.isCollapsed ? 'collapsed' : ''}" id="pattern_machine">
                <div class="machine-title">
                    <button class="collapse-btn" id="pattern_collapse" title="Réduire/Agrandir">${this.isCollapsed ? '▶' : '▼'}</button>
                    PATTERN <span class="brand">Banks A-H</span>
                </div>
                <div class="machine-content ${this.isCollapsed ? 'hidden' : ''}" id="pattern_content">
                    <div class="pattern-selector">
                        <div class="pattern-banks">
                            <div class="bank-buttons">
                                ${bankIds.map(id => `
                                    <button class="bank-btn ${id === currentPattern ? 'active' : ''}"
                                            data-pattern="${id}">${id}</button>
                                `).join('')}
                            </div>
                        </div>
                        <div class="pattern-actions">
                            <button class="action-btn copy-btn" id="patternCopy" title="Copier le pattern">
                                <span class="action-icon">📋</span>
                                <span class="action-label">COPY</span>
                            </button>
                            <button class="action-btn paste-btn ${this.store.hasClipboard() ? '' : 'disabled'}"
                                    id="patternPaste" title="Coller le pattern">
                                <span class="action-icon">📄</span>
                                <span class="action-label">PASTE</span>
                            </button>
                            <button class="action-btn clear-btn" id="patternClear" title="Effacer le pattern">
                                <span class="action-icon">🗑️</span>
                                <span class="action-label">CLEAR</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        `;

        this.setupEventListeners();
    }

    setupEventListeners() {
        // Collapse button
        const collapseBtn = this.container.querySelector('#pattern_collapse');
        if (collapseBtn) {
            collapseBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                this.toggleCollapse();
            });
        }

        // Bank buttons
        this.container.querySelectorAll('.bank-btn').forEach(btn => {
            btn.addEventListener('click', () => {
                const patternId = btn.dataset.pattern;
                this.store.switchPattern(patternId);
            });
        });

        // Copy button
        const copyBtn = this.container.querySelector('#patternCopy');
        copyBtn.addEventListener('click', () => {
            this.store.copyPattern();
            this.showFeedback(copyBtn, 'Pattern copié!');
            this.updatePasteButton();
        });

        // Paste button
        const pasteBtn = this.container.querySelector('#patternPaste');
        pasteBtn.addEventListener('click', () => {
            if (this.store.hasClipboard()) {
                this.store.pastePattern();
                this.showFeedback(pasteBtn, 'Pattern collé!');
                // Rafraîchir l'UI
                if (this.onPatternChangeCallback) {
                    this.onPatternChangeCallback(this.store.getCurrentPatternId());
                }
            }
        });

        // Clear button
        const clearBtn = this.container.querySelector('#patternClear');
        clearBtn.addEventListener('click', () => {
            if (confirm(`Effacer le pattern ${this.store.getCurrentPatternId()} ?`)) {
                this.store.clearCurrentPattern();
                this.showFeedback(clearBtn, 'Pattern effacé!');
                // Rafraîchir l'UI
                if (this.onPatternChangeCallback) {
                    this.onPatternChangeCallback(this.store.getCurrentPatternId());
                }
            }
        });
    }

    updateDisplay() {
        if (!this.container) return;

        const currentPattern = this.store.getCurrentPatternId();

        // Mettre à jour les boutons de banque
        this.container.querySelectorAll('.bank-btn').forEach(btn => {
            const isActive = btn.dataset.pattern === currentPattern;
            btn.classList.toggle('active', isActive);
        });

        this.updatePasteButton();
    }

    updatePasteButton() {
        const pasteBtn = this.container?.querySelector('#patternPaste');
        if (pasteBtn) {
            pasteBtn.classList.toggle('disabled', !this.store.hasClipboard());
        }
    }

    showFeedback(button, message) {
        const originalContent = button.innerHTML;
        button.innerHTML = `<span class="feedback">${message}</span>`;
        button.classList.add('feedback-active');

        setTimeout(() => {
            button.innerHTML = originalContent;
            button.classList.remove('feedback-active');
        }, 1000);
    }

    toggleCollapse() {
        this.isCollapsed = !this.isCollapsed;
        localStorage.setItem('pattern_collapsed', this.isCollapsed);

        const machine = this.container.querySelector('#pattern_machine');
        const content = this.container.querySelector('#pattern_content');
        const btn = this.container.querySelector('#pattern_collapse');

        if (this.isCollapsed) {
            machine.classList.add('collapsed');
            content.classList.add('hidden');
            btn.textContent = '▶';
        } else {
            machine.classList.remove('collapsed');
            content.classList.remove('hidden');
            btn.textContent = '▼';
        }
    }

    destroy() {
        if (this.unsubscribe) {
            this.unsubscribe();
        }
    }
}
